import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { LogOut, LayoutDashboard, PlusCircle, CalendarDays, GitCompareArrows, FileDown, ClipboardList, Menu, Settings as SettingsIcon, Flame } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/dashboard/stats").then(({ data }) => {
      if (alive) setStreak({ current: data?.current_streak || 0, longest: data?.longest_streak || 0, week: data?.entries_last_7_days || 0 });
    }).catch(() => {});
    return () => { alive = false; };
  }, [user, location.pathname]);

  const links = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/new", label: "New entry", icon: PlusCircle },
    { to: "/timeline", label: "Timeline", icon: CalendarDays },
    { to: "/compare", label: "Compare", icon: GitCompareArrows },
    { to: "/poem", label: "POEM", icon: ClipboardList },
  ];

  const exportPdf = async () => {
    try {
      const res = await api.get("/export/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "skintrack_report.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) { toast.error("Could not export PDF"); }
  };

  return (
    <div className="min-h-screen bg-sand-50 grain pb-20 md:pb-0">
      <nav className="sticky top-0 z-40 glass border-b border-white/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="font-serif text-xl sm:text-2xl tracking-tight text-stone-900 truncate" data-testid="shell-brand">
            Skin<span className="text-sage-400">sights</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {links.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to;
              return (
                <Link key={to} to={to} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${active ? "bg-sage-400 text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`nav-${label.toLowerCase().replace(' ','-')}`}>
                  <Icon className="w-4 h-4" />{label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={exportPdf} variant="outline" className="rounded-full hidden sm:flex border-stone-300" data-testid="export-pdf-btn">
              <FileDown className="w-4 h-4 mr-1.5"/>Export PDF
            </Button>
            <Link to="/settings" className="hidden md:inline-flex" data-testid="nav-settings">
              <Button variant="ghost" size="icon" className="rounded-full text-stone-600">
                <SettingsIcon className="w-4 h-4"/>
              </Button>
            </Link>
            <span className="text-xs text-stone-500 hidden lg:inline" data-testid="shell-user-email">{user?.email}</span>
            <Button onClick={async () => { await logout(); navigate("/"); }} variant="ghost" size="icon" className="rounded-full hidden md:inline-flex" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>

            {/* Mobile hamburger */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5"/>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-sand-50 p-6">
                <div className="font-serif text-2xl text-stone-900 mb-1">Skin<span className="text-sage-400">sights</span></div>
                <p className="text-xs text-stone-500 mb-6 truncate">{user?.email}</p>
                <nav className="space-y-1">
                  {links.map(({ to, label, icon: Icon }) => {
                    const active = location.pathname === to;
                    return (
                      <Link key={to} to={to} onClick={() => setOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-base transition-all ${active ? "bg-sage-400 text-white" : "text-stone-700 hover:bg-stone-100"}`} data-testid={`mobile-nav-${label.toLowerCase().replace(' ','-')}`}>
                        <Icon className="w-5 h-5" />{label}
                      </Link>
                    );
                  })}
                </nav>
                <div className="mt-8 pt-6 border-t border-stone-200 space-y-2">
                  <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-2xl text-base text-stone-700 hover:bg-stone-100 transition-colors" data-testid="mobile-nav-settings">
                    <SettingsIcon className="w-5 h-5"/>Settings
                  </Link>
                  <Button onClick={() => { setOpen(false); exportPdf(); }} variant="outline" className="w-full rounded-full border-stone-300 justify-start" data-testid="mobile-export-pdf-btn">
                    <FileDown className="w-4 h-4 mr-2"/>Export PDF report
                  </Button>
                  <Button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} variant="ghost" className="w-full rounded-full justify-start text-stone-600" data-testid="mobile-logout-btn">
                    <LogOut className="w-4 h-4 mr-2"/>Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">{children}</main>

      {/* Mobile FAB — quick "Log today" with date prefill */}
      <div className="md:hidden fixed bottom-5 right-5 z-30 flex flex-col items-end gap-2">
        {streak && streak.current > 0 && (
          <div
            data-testid="mobile-streak-chip"
            className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-full bg-white shadow-md text-stone-800 streak-chip-pop"
            style={{ border: "1px solid #fbd9b6" }}
            title={streak.longest > streak.current ? `Best streak: ${streak.longest} days` : "Keep it going!"}
          >
            <Flame
              className="w-4 h-4 streak-flame-pulse"
              style={{ color: "#e07b3a" }}
              data-testid="mobile-streak-flame"
              strokeWidth={2.4}
            />
            <span className="text-xs font-semibold leading-none">
              {streak.current}-day streak
            </span>
          </div>
        )}
        <Link
          to={`/new?date=${todayKey()}`}
          data-testid="mobile-fab-log-today"
          aria-label="Log today's entry"
          className="group inline-flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-full bg-sage-400 hover:bg-sage-500 active:scale-95 text-white shadow-[0_10px_25px_-5px_rgba(59,86,46,0.45)] transition-all"
        >
          <PlusCircle className="w-5 h-5 shrink-0" />
          <span className="font-medium text-sm">Log today</span>
        </Link>
      </div>
    </div>
  );
}

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
